import React, { useState, useEffect, useContext } from 'react';

import styled from 'styled-components';

import baseConstants from '@constants/baseConstants';

import { showDateNoTimeAsDateOnly } from '@libs/NwMoment';
import { getCustomFieldValue } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { useContact } from '@hooks/API/Contact/UseContact';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import AnagraphicalForm from '@components/ContactDetails/CommonComponents/Anagraphical/AnagraphicalForm';

import CountryLabel from '@components/Gui/CountryLabel';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwEditButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DetailItemContainer = styled(FlexContainer)`
    padding: 4px 0;
`;

const DetailItemLabel = styled.div`
    min-width: 120px;
    //white-space: nowrap;
    margin-right: 8px;
    font-size: 0.9rem;
    text-transform: uppercase;
`;

const DetailItemValue = styled.div`
    font-size: 0.9rem;
    font-weight: bold;
`;

const AnagraphicalDrawer = ({ currentContact, readOnly, onClose, type }) => {
    const [isEdit, setIsEdit] = useState(false);
    const [customFields, setCustomFields] = useState([]);
    const [validCustomFieldsKeys, setValidCustomFieldsKeys] = useState([]);
    const { cachedList } = useContext(ListContext);
    const [touchForm, setTouchForm] = useState(false);
    const breakpoints = useNwBreakPoints();

    const Country = currentContact.DefaultConnection ? currentContact.DefaultConnection.Country : '';
    const Email = currentContact.DefaultConnection ? currentContact.DefaultConnection.Email : '';

    const {
        Name,
        Surname,
        Code,
        LegalName,
        LegalSurname,
        BirthDate,
        BirthPlace,
        NationalityCode,
        CitizenshipCode,
        Citizenship2Code,
        GenderValue,
    } = currentContact;

    const dataFields = {
        Name,
        Surname,
        'Legal Name': LegalName,
        'Legal Surname': LegalSurname,
        Code,
        Email,
        Country,
        'Birth Date': showDateNoTimeAsDateOnly(BirthDate),
        'Birth Place': BirthPlace,
        Nationality: NationalityCode,
        Citizenship: CitizenshipCode,
        Citizenship2: Citizenship2Code,
        Gender: GenderValue,
    };

    useEffect(() => {
        if (currentContact) {
            const contactType = currentContact.ContactType;
            const category =
                contactType === 'Model' ? 'Model' : baseConstants.CUSTOMER_CATEGORIES[currentContact.SubType - 1];
            const customFields = cachedList.customFields.items[category];
            setCustomFields(customFields);
            setValidCustomFieldsKeys(customFields.filter((field) => !field.Disabled).map((field) => field.Name));
        }
    }, [currentContact, cachedList]);

    const isModel = type === 1;
    const drawerTitle = isModel ? 'Model details' : 'Contact details';
    const editDrawerTitle = isModel ? 'Edit Model Details' : 'Edit Contact Details';

    const handleEditContactDetail = () => {
        setIsEdit(true);
    };

    const handleCloseContactDetail = () => {
        setIsEdit(false);
    };

    const ItemValue = ({ name, value }) => {
        return (
            <DetailItemValue>
                {name === 'Nationality' || name === 'Citizenship' || name === 'Citizenship2' ? (
                    value ? (
                        <CountryLabel code={value} flag />
                    ) : (
                        <>n.d.</>
                    )
                ) : (
                    <div>{value}</div>
                )}
            </DetailItemValue>
        );
    };

    return (
        <NwDrawer onClose={onClose} title={drawerTitle} whiteBg={false}>
            <div className="drawer-content standard">
                <NwCard
                    title="Info"
                    extra={<NwEditButton ghost icon={byPrefixAndName.fasl['pen']} onClick={handleEditContactDetail} />}
                >
                    {Object.keys(dataFields).map((fieldKey) => {
                        return (
                            dataFields[fieldKey] && (
                                <DetailItemContainer
                                    column={breakpoints === 1}
                                    alignItems={breakpoints === 1 && 'flex-start'}
                                    key={`event-detail-${fieldKey}`}
                                >
                                    <DetailItemLabel>{fieldKey}</DetailItemLabel>
                                    <ItemValue name={fieldKey} value={dataFields[fieldKey]} />
                                </DetailItemContainer>
                            )
                        );
                    })}

                    {currentContact.CustomFields && currentContact.CustomFields.length > 0 && (
                        <>
                            <NwDivider>additional info</NwDivider>
                            {Object.keys(currentContact.CustomFields).map((key, index) => {
                                if (validCustomFieldsKeys && validCustomFieldsKeys.includes(key)) {
                                    return (
                                        <DetailItemContainer
                                            column={breakpoints === 1}
                                            alignItems={breakpoints === 1 && 'flex-start'}
                                            key={`custom-field-${key}`}
                                        >
                                            <DetailItemLabel>{key.replace(/_/g, ' ')}</DetailItemLabel>
                                            <ItemValue
                                                name=""
                                                value={getCustomFieldValue(
                                                    key,
                                                    currentContact.CustomFields[key],
                                                    customFields
                                                )}
                                            />
                                        </DetailItemContainer>
                                    );
                                }
                            })}
                        </>
                    )}
                </NwCard>
            </div>
            {isEdit && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    title={editDrawerTitle}
                    onClose={handleCloseContactDetail}
                >
                    <AnagraphicalForm
                        onTouchForm={setTouchForm}
                        touchForm={touchForm}
                        currentContact={currentContact}
                        customFields={customFields}
                        type={type}
                        onCancel={handleCloseContactDetail}
                    />
                </NwDrawer>
            )}
        </NwDrawer>
    );
};

export default AnagraphicalDrawer;
